import keyGenerator from 'utils/keys';
import isDateToday from 'utils/dateAndTimes/methods/isDateToday';

import { constants as orderConstants } from 'utils/order';

const SESSION_STORAGE_KEY = 'sessionId';

const SHIPPER_FORM_STARTED = 'shipper_form_started';
const SHIPPER_FORM_CONFIRMED = 'shipper_form_confirmed';
const SHIPPER_FORM_SUBMITTED = 'shipper_form_submitted';
const SHIPPER_FORM_CARD = 'shipper_form_card';
const SHIPPER_FORM_ORDER_TYPE_CHANGED = 'shipper_form_order_type_changed';
const SA_CLICK_WEEKDAY_IN_RECURRING_ORDER_CONFIGURATION =
  'SA_click_weekday_in_recurring_order_configuration';
const PRICE_QUOTE_SHOWN = 'price_quote_shown';
const SA_PRICING_DOUBTS_WHY_EXTRAS_IN_PRICE =
  'SA_pricing_doubts_why_extras_in_price';

// TECH-DEBT
const prepareForSlackQuote = (body, quote) => {
  try {
    const shipment = body.shipments[0];

    const pickup_zip_code = shipment.address_from.zipcode;
    const dropoff_zip_code = shipment.address_to.zipcode;

    const isToday = shipment.pickup_date_from
      ? isDateToday(shipment.pickup_date_from, shipment.pickup_timezone)
      : true;
    const pickup_day = isToday ? 'today' : 'tomorrow or other';
    const loadType = [...new Set(shipment.loads.map((load) => load.load_type))];
    const typeOfGoods = [
      ...new Set(
        shipment.loads
          .map((load) => load.type_of_goods)
          .filter((el) => el !== undefined)
      ),
    ];

    return {
      ...(shipment && shipment.loads && shipment.loads[0]
        ? shipment.loads[0]
        : {}),
      ...shipment,
      pickup_zip_code,
      dropoff_zip_code,
      net_full_price: quote && quote.net_full_price,
      liftgate_needed: (shipment.extras || []).indexOf('LIFTGATE') >= 0,
      side_load: (shipment.extras || []).indexOf('SIDE_LOAD') >= 0,
      handling_loading:
        (shipment.extras || []).indexOf('HANDLING_LOADING') >= 0,
      handling_unloading:
        (shipment.extras || []).indexOf('HANDLING_UNLOADING') >= 0,
      pickup_day,
      flow: 'shipper_form_quote',
      load_type: loadType,
      type_of_goods: typeOfGoods,
    };
  } catch (err) {
    return {};
  }
};

const prepareEventBody = (body, quote) => prepareForSlackQuote(body, quote);

const parseShipments = (shipments) => {
  const convertDateToTimeStamp = (date) => {
    if (!date || !date.valueOf) return date;
    return date.valueOf();
  };
  if (!shipments) return [];
  return shipments.map((shipment) => {
    return {
      ...shipment,
      ...(shipment.dropoff_time_from && {
        dropoff_time_from: convertDateToTimeStamp(shipment.dropoff_time_from),
      }),
      ...(shipment.dropoff_time_to && {
        dropoff_time_to: convertDateToTimeStamp(shipment.dropoff_time_to),
      }),
      ...(shipment.pickup_time_from && {
        pickup_time_from: convertDateToTimeStamp(shipment.pickup_time_from),
      }),
      ...(shipment.pickup_time_to && {
        pickup_time_to: convertDateToTimeStamp(shipment.pickup_time_to),
      }),
    };
  });
};

const session = {
  create: (user) => {
    const userId = (user && user.id) || 'no_user';
    return `${userId}_${keyGenerator.timestamp()}`;
  },
  save: (sessionId) => {
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  },
  get: () => localStorage.getItem(SESSION_STORAGE_KEY),
};

const form = {
  started: ({ type, user }) => {
    const sessionId = session.create(user);
    session.save(sessionId);
    return {
      eventName: SHIPPER_FORM_STARTED,
      eventData: { type: orderConstants.getTypeForEvent(type), sessionId },
    };
  },

  orderTypeChanged: ({ type }) => ({
    eventName: SHIPPER_FORM_ORDER_TYPE_CHANGED,
    eventData: { type: orderConstants.getTypeForEvent(type) },
  }),

  recurringWeekdayConfigurationChanged: ({ days }) => ({
    eventName: SA_CLICK_WEEKDAY_IN_RECURRING_ORDER_CONFIGURATION,
    eventData: { days },
  }),

  priceQuoteShown: ({
    quoteResponse,
    quoteBody,
    type,
    isEditing,
    pricing_strategy,
    productType,
  }) => {
    const parsedBody = {
      ...quoteBody,
      shipments: parseShipments(quoteBody.shipments),
    };
    return {
      eventName: PRICE_QUOTE_SHOWN,
      eventData: {
        quoteBody: parsedBody,
        quoteResponse: {
          ...quoteResponse,
          ontruck_service:
            quoteResponse?.feasibility?.service_type ===
            orderConstants.SERVICE_TYPE_LH_EXPRESS
              ? orderConstants.SERVICE_TYPE_LH_EXPRESS
              : null,
        },
        isEditing,
        type: orderConstants.getTypeForEvent(type),
        pricing_strategy,
        productType,
        ...prepareForSlackQuote(parsedBody, quoteResponse),
      },
    };
  },

  shipperFormConfirmed: ({ type, body, quote }) => {
    const parsedBody = {
      ...body,
      shipments: parseShipments(body.shipments),
    };
    return {
      eventName: SHIPPER_FORM_CONFIRMED,
      eventData: {
        type: orderConstants.getTypeForEvent(type),
        ...prepareEventBody(parsedBody, quote),
      },
    };
  },

  shipperFormCard: ({ type }) => ({
    eventName: SHIPPER_FORM_CARD,
    eventData: { type: orderConstants.getTypeForEvent(type) },
  }),

  shipperFormSubmitted: ({ type, body, quote }) => {
    const parsedBody = {
      ...body,
      shipments: parseShipments(body.shipments),
    };
    return {
      eventName: SHIPPER_FORM_SUBMITTED,
      eventData: {
        type: orderConstants.getTypeForEvent(type),
        ...prepareEventBody(parsedBody, quote),
      },
    };
  },

  saPricingDoubtsWhyExtrasInPrice: () => ({
    eventName: SA_PRICING_DOUBTS_WHY_EXTRAS_IN_PRICE,
    eventData: {},
  }),
};

export default form;
export { session, parseShipments };
